"use client";

import * as React from "react";
import {
  Text,
  Section,
  SectionCard,
  CardTitle,
  CardDescription,
  Button,
  Badge,
} from "@/src/components";
import { SelectBlock } from "@/src/components/input/select-advanced";

const THEMES = [
  { value: "theme-modern", label: "Asymmetric (Default)", description: "Current styling - px > py for horizontal emphasis" },
  { value: "theme-sharp", label: "Symmetric", description: "Uniform padding - px equals py, balanced appearance" },
  { value: "theme-light", label: "Asymmetric Spacious", description: "Generous asymmetric - more breathing room" },
  { value: "theme-dark", label: "Symmetric Spacious", description: "Generous symmetric - balanced and roomy" },
  { value: "theme-minimal", label: "Compact", description: "Tight spacing - maximum content density" },
  { value: "theme-elevated", label: "Elevated", description: "Premium feel - generous spacing with depth" },
  { value: "theme-glass", label: "Glassmorphic", description: "Frosted glass with translucent surfaces and blur" },
  { value: "theme-neumorphic", label: "Neumorphic", description: "Soft embossed 3D with inset/outset shadows" },
  { value: "theme-high-contrast", label: "High Contrast", description: "Bold borders, maximum readability (WCAG AAA)" },
  { value: "theme-warm", label: "Warm", description: "Cream/beige palette — premium warm finance aesthetic" },
];

const SAMPLE_DATA = [
  { id: 1, name: "Alex Johnson", email: "alex@example.com", role: "Admin", status: "Active", joined: "2024-01-15" },
  { id: 2, name: "Sarah Chen", email: "sarah@example.com", role: "Editor", status: "Active", joined: "2024-02-20" },
  { id: 3, name: "Mike Peters", email: "mike@example.com", role: "Viewer", status: "Pending", joined: "2024-03-10" },
  { id: 4, name: "Emily Davis", email: "emily@example.com", role: "Editor", status: "Active", joined: "2024-03-22" },
  { id: 5, name: "James Wilson", email: "james@example.com", role: "Viewer", status: "Inactive", joined: "2024-04-05" },
];

const STATS = [
  { title: "Total Users", value: "12,847", change: "+12.5%", positive: true },
  { title: "Active Sessions", value: "3,421", change: "+8.2%", positive: true },
  { title: "Bounce Rate", value: "24.3%", change: "-2.1%", positive: true },
  { title: "Avg. Duration", value: "4m 32s", change: "-0.8%", positive: false },
];

export default function DocsThemes() {
  const [currentTheme, setCurrentTheme] = React.useState("theme-modern");

  const currentThemeInfo = THEMES.find(t => t.value === currentTheme);

  return (
    <>
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Spacing System Test
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Test how well we can control the design token system. 6 themes with different
            spacing approaches: asymmetric vs symmetric, compact vs spacious.
          </Text>
        </div>
      </Section>

      {/* Theme Selector */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Select Theme</CardTitle>
              <CardDescription>Choose a theme to preview below</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="w-full sm:w-72">
              <SelectBlock
                options={THEMES.map(t => ({ value: t.value, label: t.label }))}
                value={{ value: currentTheme, label: currentThemeInfo?.label || "" }}
                onChange={(option) => {
                  const selected = option as { value: string } | null;
                  setCurrentTheme(selected?.value || "theme-modern");
                }}
                placeholder="Select theme..."
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {currentThemeInfo?.description}
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Theme Preview Area */}
      <Section>
        <div className="space-y-2 mb-4">
          <Text as="h2" className="text-h4 font-semibold">
            Live Preview
          </Text>
          <Text className="text-sm text-muted-foreground">
            Components below are rendered with the selected theme applied
          </Text>
        </div>

        {/* Theme Container */}
        <div
          className={`${currentTheme} rounded-2xl border-2 border-dashed border-border p-6 space-y-8 transition-all duration-300`}
          style={{ background: "var(--theme-surface-bg, var(--background))" }}
        >
          {/* Stats Cards Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((stat) => (
              <div key={stat.title} className="theme-card">
                <div className="theme-card-description text-xs uppercase tracking-wide">
                  {stat.title}
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-2xl font-bold">{stat.value}</span>
                  <span className={`text-sm font-medium ${stat.positive ? "text-success" : "text-destructive"}`}>
                    {stat.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Content Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Card 1: Simple Card */}
            <div className="theme-card">
              <div className="theme-card-header">
                <div className="theme-card-title">Project Overview</div>
                <div className="theme-card-description">
                  A summary of your current project status and key metrics.
                </div>
              </div>
              <div className="theme-card-content space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Completion</span>
                  <span className="text-sm font-semibold">78%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: "78%", borderRadius: "var(--theme-radius-full, 9999px)" }}
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button className="theme-button theme-button-primary text-sm">
                    View Details
                  </button>
                  <button className="theme-button border text-sm">
                    Export
                  </button>
                </div>
              </div>
            </div>

            {/* Card 2: Card with List */}
            <div className="theme-card">
              <div className="theme-card-header">
                <div className="theme-card-title">Recent Activity</div>
                <div className="theme-card-description">
                  Latest updates from your team members.
                </div>
              </div>
              <div className="theme-card-content">
                <div className="space-y-3">
                  {[
                    { user: "Alex", action: "pushed to main", time: "2m ago" },
                    { user: "Sarah", action: "commented on PR #42", time: "15m ago" },
                    { user: "Mike", action: "created new issue", time: "1h ago" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: "var(--theme-border-color)" }}>
                      <div>
                        <span className="font-medium">{item.user}</span>
                        <span className="text-sm opacity-70"> {item.action}</span>
                      </div>
                      <span className="text-xs opacity-50">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="theme-card !p-0 overflow-hidden">
            <div className="p-4 border-b" style={{ borderColor: "var(--theme-border-color)" }}>
              <div className="theme-card-title">Team Members</div>
              <div className="theme-card-description">
                Manage your team and their access levels.
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: "var(--theme-surface-bg)" }}>
                    <th className="theme-table-header text-left">Name</th>
                    <th className="theme-table-header text-left">Email</th>
                    <th className="theme-table-header text-left">Role</th>
                    <th className="theme-table-header text-left">Status</th>
                    <th className="theme-table-header text-left">Joined</th>
                    <th className="theme-table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_DATA.map((row) => (
                    <tr key={row.id} className="theme-table-row">
                      <td className="theme-table-cell font-medium">{row.name}</td>
                      <td className="theme-table-cell opacity-70">{row.email}</td>
                      <td className="theme-table-cell">
                        <span className="theme-badge bg-primary/10 text-primary">
                          {row.role}
                        </span>
                      </td>
                      <td className="theme-table-cell">
                        <span className={`theme-badge ${
                          row.status === "Active"
                            ? "bg-success-bg text-success"
                            : row.status === "Pending"
                            ? "bg-warning-bg text-warning"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="theme-table-cell opacity-70">{row.joined}</td>
                      <td className="theme-table-cell text-right">
                        <button className="theme-button text-sm px-2 py-1 hover:bg-accent">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Form Elements Row */}
          <div className="theme-card">
            <div className="theme-card-header">
              <div className="theme-card-title">Form Elements</div>
              <div className="theme-card-description">
                Input fields and buttons with theme styling.
              </div>
            </div>
            <div className="theme-card-content">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email Address</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className="theme-input w-full bg-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="theme-input w-full bg-transparent"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button className="theme-button theme-button-primary">
                  Submit
                </button>
                <button className="theme-button border">
                  Cancel
                </button>
                <button className="theme-button opacity-50">
                  Disabled
                </button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Theme Comparison */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Theme Comparison</CardTitle>
              <CardDescription>Quick visual comparison of all 6 themes</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {THEMES.map((theme) => (
              <button
                key={theme.value}
                onClick={() => setCurrentTheme(theme.value)}
                className={`${theme.value} p-4 rounded-lg border-2 transition-all ${
                  currentTheme === theme.value
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-transparent hover:border-border"
                }`}
                style={{ background: "var(--theme-surface-bg, var(--background))" }}
              >
                <div
                  className="h-16 rounded mb-2"
                  style={{
                    background: "var(--theme-card-bg, white)",
                    boxShadow: "var(--theme-shadow, none)",
                    borderRadius: "var(--theme-radius-lg, 0.5rem)",
                    border: "var(--theme-border-width, 1px) solid var(--theme-border-color, #e5e7eb)"
                  }}
                />
                <div className="text-xs font-medium truncate">{theme.label.split(" / ")[0]}</div>
              </button>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Implementation Notes */}
      <Section>
        <SectionCard
          header={<CardTitle>Implementation</CardTitle>}
          contentClassName="p-6"
        >
          <div className="space-y-4 text-sm">
            <div>
              <Text className="font-semibold mb-2">How it works:</Text>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Themes are defined as CSS classes with CSS custom properties</li>
                <li>Each theme overrides variables for radius, spacing, shadows, and colors</li>
                <li>Components use <code className="px-1 py-0.5 bg-muted rounded text-xs">theme-*</code> classes that reference these variables</li>
                <li>Switching themes is as simple as changing the container class</li>
              </ul>
            </div>
            <div>
              <Text className="font-semibold mb-2">Usage:</Text>
              <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-xs">
{`// Wrap your content with a theme class
<div className="theme-modern">
  <div className="theme-card">
    <div className="theme-card-title">Title</div>
    <div className="theme-card-content">Content</div>
  </div>
</div>`}
              </pre>
            </div>
          </div>
        </SectionCard>
      </Section>
    </>
  );
}
